import React, { useContext, useEffect, useMemo, useRef, useState } from "react";
import { createPortal } from "react-dom";
import ReportDateField from "./ReportDateField";
import { useNavigate, useParams } from "react-router-dom";
import styles from "./styles/AssetDashboard.module.css";
/*
  The page heading and the crumbs are Asset Management's, taken from its own
  stylesheet rather than restated here. The two screens are one place with two
  views, so switching between them must not shift the header by a pixel — and a
  second copy of those rules would drift the moment either side was touched.
*/
import amStyles from "../Asset Management/styles/AssetManagement.module.css";
import {
  ExecutiveSummary,
  WealthSummary,
  ExposureSections,
  IncomeSection,
  EstateSection,
  RecommendationsSection,
} from "./ReportSections";
import { LanguageContext } from "../../../Context/changeLanguageProvider";
import { UserData } from "../../../Context/DataContext";
import { customerPath } from "../PartnerNav/partnerNav";
import { active } from "../../../Utils/partnerApproval";
import { CURRENCY_OPTIONS } from "../Asset Evaluation/AssetEvaluation";
import { fetchCurrencyRates } from "../../../Utils/assetEvaluationUtils";
import { firstEvaluationDateOf } from "./reportMetrics";
import SectionSelector from "./SectionSelector";
import { downloadElementPdf } from "../../../Utils/downloadElementPdf";
import { PageTabs } from "../PartnerNav/PageTabs";
import {
  useCustomerHeaderSlot,
  useInCustomerProfile,
  usePartnerView,
} from "../PartnerNav/partnerNav";
import partnerNavStyles from "../PartnerNav/styles/PartnerNav.module.css";

/*
  The Wealth Report — reached from the "Dashboard" crumb on Asset Management.

  This file is the report's header: who it is for, and the three controls that
  scope everything below it. The sections that follow are being added one at a
  time; they all read `range` and `currency` from here, which is why those live
  on this component rather than inside the header card.

  Nothing here needs the backend. The counts, the name and the photo are
  already on `UserData` — the same three figures the inheritance tree's "You"
  node shows, deliberately read from the same place so the two cannot disagree.
*/

const FALLBACK = {
  assetDashboardText1: "Wealth Report",
  assetDashboardText2:
    "A comprehensive analysis of your wealth and personalized recommendations",
  assetDashboardText3: "Wealth & Inheritance",
  assetDashboardText4: "From date:",
  assetDashboardText5: "Till:",
  assetDashboardText6: "Currency",
  assetDashboardText7: "The beginning of time",
  assetDashboardText8: "Today",
  assetDashboardText9: "Last 12 months",
  assetDashboardText10: "Last 6 months",
  assetDashboardText11: "Year to date",
  assetDashboardText12: "Custom…",
  assetDashboardText90: "Ostreon Wealth Heritage",
  assetDashboardText91:
    "Confidential report based on information provided by the client.",
  assetDashboardText92:
    "This data is encrypted with your private key. OSTREON can't see any of this data.",
  assetDashboardText102: "Report date:",
  assetDashboardText103: "Page %1 of %2",
  assetDashboardText104: "Customizing Your Financial Report",
  assetDashboardText105: "Report will include these chapters:",
  assetDashboardText106: "No",
  assetDashboardText107: "Yes",
  assetDashboardText108: "Close",
};

// The sentinel that swaps a preset dropdown for a date field.
export const RANGE_CUSTOM = "custom";
// "From" open-ended, i.e. every record there has ever been.
export const RANGE_START = "start";
// "Till" open-ended, i.e. up to and including today.
export const RANGE_NOW = "now";

const pad = (n) => String(n).padStart(2, "0");
const toInputDate = (d) =>
  `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}`;

/*
  A preset resolved to the date it means, or "" for the open-ended ends. The
  report reads these, never the preset key — so a section only ever deals with
  two dates, whichever way the user chose them.
*/
export const resolveRangeDate = (preset, custom) => {
  const now = new Date();
  switch (preset) {
    case RANGE_CUSTOM:
      return custom || "";
    case RANGE_NOW:
      return toInputDate(now);
    case "last12":
      return toInputDate(
        new Date(now.getFullYear() - 1, now.getMonth(), now.getDate()),
      );
    case "last6":
      return toInputDate(
        new Date(now.getFullYear(), now.getMonth() - 6, now.getDate()),
      );
    case "ytd":
      return toInputDate(new Date(now.getFullYear(), 0, 1));
    case RANGE_START:
    default:
      return "";
  }
};

function AssetDashboard() {
  // Only a partner sees the wider title spacing; see PartnerNav.
  const partnerView = usePartnerView();
  const inCustomerProfile = useInCustomerProfile();
  /*
    Inside a profile the report button belongs on the profile's own header line,
    beside "Back to customer List" -- see CUSTOMER_HEADER_ACTIONS_ID. Null until
    that header has mounted, and null outside a profile, in which case the
    button stays where it has always been.
  */
  const headerSlot = useCustomerHeaderSlot(inCustomerProfile);
  const navigate = useNavigate();
  const userData = UserData();
  const websiteCurrentLang = useContext(LanguageContext).language;

  const [content, setContent] = useState(
    websiteCurrentLang?.content?.User?.AssetDashboard?.AssetDashboard ||
      FALLBACK,
  );
  useEffect(() => {
    setContent(
      websiteCurrentLang?.content?.User?.AssetDashboard?.AssetDashboard ||
        FALLBACK,
    );
  }, [websiteCurrentLang]);
  const t = (key) => (content && content[key]) || FALLBACK[key];
  /*
    The section titles live in this same translation block but not in the
    FALLBACK above, which only carries what the header card needs before the
    language file has loaded. The picker names every chapter, so it brings
    its own last resort for the few seconds that matters.
  */
  const tr = (key, fallback) => t(key) || fallback;

  /*
    The page title and the crumbs belong to Asset Management, and are read from
    there rather than restated here — the two screens are one place with two
    views, and a second copy of "Asset Management" would drift.
  */
  const am =
    websiteCurrentLang?.content?.User?.AssetManagement?.AssetManagement;
  const tAm = (key, fallback) => (am && am[key]) || fallback;

  // The same three labels the inheritance tree's "You" node uses.
  const tree = websiteCurrentLang?.content?.User?.InheritanceTree?.You;
  const dup = websiteCurrentLang?.content?.DublicatedText;

  /*
    Every figure on this page is a summary -- counts, values, the charts -- and
    a partner-created asset the customer has not approved counts towards none of
    them. Excluded once, here, rather than at each of the dozen places that read
    root.assets: this page shows no rows, so there is nothing that needs to see
    a draft in order to review it.
  */
  /*
    Set only when this page renders inside a customer's profile, from the
    /user/customers/:id/:tab route. Empty on the partner's own wealth report.
  */
  const { id: customerId } = useParams();
  const rootSource = (userData && userData.UserData) || {};
  const root = useMemo(
    () => ({ ...rootSource, assets: active(rootSource.assets) }),
    [rootSource]
  );
  const customer = (userData && userData.CustomerUser) || {};

  const [fromPreset, setFromPreset] = useState(RANGE_START);
  const [fromCustom, setFromCustom] = useState("");
  const [tillPreset, setTillPreset] = useState(RANGE_NOW);
  const [tillCustom, setTillCustom] = useState("");
  /*
    Opens on the currency the account is billed in, the same default the asset
    modal takes, so the first read of the report is in the money the user
    thinks in.
  */
  const [currency, setCurrency] = useState(
    String(customer?.currency || root?.currency || "eur").toLowerCase(),
  );

  /*
    The USD-based rate table. Records carry their own currency, so a report read
    in one currency must convert every figure recorded in another before adding
    anything up. Null until it arrives; on failure amounts are summed as entered
    rather than the report refusing to draw.

    The endpoint answers `{ base, rates }` and it is the inner map that is
    wanted — convertAmount looks a currency up as `rates[code]`. Holding the
    envelope instead made every lookup undefined, which convertAmount treats as
    "no rate available" and returns the amount untouched: not an error anywhere,
    just a whole report silently adding francs to dollars.
  */
  // How many exposure sections appeared, so the ones after them can be numbered.
  // The slice of the page the PDF is made from; see the button above.
  const reportRef = useRef(null);
  // True only while the page is being photographed; see the button.
  // Only the button reads this now — the page itself does not change.
  const [busy, setBusy] = useState(false);
  /*
    The date the document was produced, stamped when the button is pressed
    rather than when the page loaded — a report left open overnight would
    otherwise go out under yesterday's date.
  */
  const [reportDate, setReportDate] = useState("");
  /*
    The exposure sections, as the report actually has them — the page numbers
    what follows from their count, and the chapter picker lists them by name.
  */
  const [exposureSections, setExposureSections] = useState([]);
  const exposureCount = exposureSections.length;
  // Open while the reader is choosing chapters; null the rest of the time.
  const [picking, setPicking] = useState(false);
  const [rates, setRates] = useState(null);
  useEffect(() => {
    let cancelled = false;
    fetchCurrencyRates()
      .then((data) => {
        if (!cancelled) setRates(data?.rates || null);
      })
      .catch(() => {});
    return () => {
      cancelled = true;
    };
  }, []);

  /*
    What "the beginning of time" actually names: the day the first asset was
    ever valued, not the day the account was opened. The filter stays
    open-ended either way — an unbounded start and a start on the earliest
    record select the same records — so this is only ever shown, never applied.
  */
  const firstEvaluation = useMemo(
    () => firstEvaluationDateOf(root.assets),
    [root.assets],
  );

  /*
    A range end as the PDF has to say it.

    On screen "Today" and "Last 12 months" are fine: the reader is looking at
    them now, so now is obvious. A file opened in six months has lost that —
    "Today" would name a day the reader has no way to recover. So the document
    carries the date the preset resolved to at the moment it was produced, and
    the preset's name is dropped entirely.

    The open-ended start has no resolved date of its own, being the absence of a
    bound; it prints the first valuation instead, which is the earliest date the
    report could have anything to say about. With nothing valued at all there is
    no such date, and the phrase stands as the only honest answer.
  */
  const rangeText = (preset, custom, openLabelKey) => {
    const stored =
      preset === RANGE_START ? firstEvaluation : resolveRangeDate(preset, custom);
    /*
      Read as three numbers rather than split on a separator, because the two
      sources do not agree on one: a resolved preset is YYYY-MM-DD, while a
      valuation record is stored YYYY/MM/DD. Splitting on the hyphen parsed the
      record to NaN and quietly fell through to the preset's name — which is the
      one value that has to be a date, since it is the only one the reader
      cannot work out for themselves.

      The same reading `dateKey` and `recordDateKey` take, for the same reason.
    */
    const parts = String(stored || "").match(/\d+/g);
    if (!parts || parts.length < 3) return t(openLabelKey);
    const [y, m, d] = parts.map(Number);
    if (!y || !m || !d) return t(openLabelKey);
    /*
      Numbers in both languages, each in its own order: MM/DD/YYYY in English,
      DD/MM/YYYY in French. The locale decides the order, so the option list is
      the same either way -- asking for it explicitly rather than leaning on the
      locale's default short form, which is the runtime's choice and would let a
      single-digit day read 5/09 on one machine and 05/09 on another.
    */
    const french = websiteCurrentLang?.label === "fr";
    return new Date(y, m - 1, d).toLocaleDateString(
      french ? "fr-FR" : "en-US",
      { month: "2-digit", day: "2-digit", year: "numeric" },
    );
  };

  /*
    What the sections are given: two plain dates and a currency, whichever way
    the user arrived at them. Resolved here once rather than in each of them.
  */
  const range = useMemo(
    () => ({
      from: resolveRangeDate(fromPreset, fromCustom),
      till: resolveRangeDate(tillPreset, tillCustom),
      currency,
    }),
    [fromPreset, fromCustom, tillPreset, tillCustom, currency],
  );

  const fullName =
    String(root.customer_full_name || "").trim() ||
    String(customer?.customer_full_name || "").trim();

  /*
    The stored avatar is raw base64 whose first character gives away the format
    — the same two-line test uploadAvatar and Beneficiarytrustee both make.
  */
  const avatar = root.profile_avatar;
  let extension;
  if (avatar)
    extension = avatar[0] === "/" ? ".jpg" : avatar[0] === "i" ? ".png" : "";
  const initials = fullName
    .split(" ")
    .filter(Boolean)
    .map((part) => part[0])
    .join("")
    .slice(0, 3)
    .toUpperCase();

  const counts = [
    {
      key: "assets",
      icon: styles.icon_assets,
      value: (root.assets || []).length,
      label: (tree && tree.youText5) || "Assets",
      to: "/user/assetmanagement",
    },
    {
      key: "beneficiaries",
      icon: styles.icon_people,
      value: (root.beneficiaries || []).length,
      label: (tree && tree.youText6) || "Beneficiaries",
      to: "/user/beneficiaries",
    },
    {
      key: "trustees",
      icon: styles.icon_people,
      value: (root.trustees || []).length,
      label: (dup && dup.dublicatedText64) || "Trusted Advisors",
      to: "/user/advisors",
    },
  ];

  /*
    The report's chapters in the order the document has them, which is the order
    the picker lists them in. The asset-type sections are however many the
    account turned out to have, so the numbering of everything after them is
    read off that count rather than assumed.
  */
  const reportChapters = [
    { key: "executive", title: tr("assetDashboardText13", "Executive Summary") },
    { key: "wealth", title: tr("assetDashboardText33", "Wealth Summary") },
    ...exposureSections,
    { key: "income", title: tr("assetDashboardText48", "Income (annual)") },
    {
      key: "estate",
      title: tr("assetDashboardText55", "Estate & Succession Planning"),
    },
    {
      key: "recommendations",
      title: tr("assetDashboardText75", "Strategic Recommendations"),
    },
  ].map((chapter, i) => ({ ...chapter, number: i + 1 }));

  /*
    Produce the file. `omit` is the keys the reader switched off; each becomes a
    `data-report-section` selector the capture drops from its copy of the page.

    What turns the page into a document — the pinned width, the unfolded
    sections, the hidden toggles, the issue date — is applied to that copy, not
    to this one, so nothing on screen moves. The frame's wait is only so React
    has committed the date before the copy is taken.
  */
  const generate = async (omit) => {
    setReportDate(
      new Date().toLocaleDateString(
        websiteCurrentLang?.label === "fr" ? "fr-FR" : "en-US",
        { year: "numeric", month: "long", day: "numeric" }
      )
    );
    setBusy(true);
    await new Promise((done) =>
      window.requestAnimationFrame(() =>
        window.requestAnimationFrame(done)
      )
    );
    try {
      await downloadElementPdf(reportRef.current, "wealth-report.pdf", {
        captureClass: styles.capturing,
        omitSelector: omit
          .map((key) => `[data-report-section="${key}"]`)
          .join(","),
        /*
          The closing strip is drawn onto the PDF rather than captured with the
          page, so its wording is handed over here — the util has no language of
          its own — and its counter is filled in per page once the pagination
          has settled.
        */
        footer: {
          brand: t("assetDashboardText90"),
          note: [t("assetDashboardText91"), t("assetDashboardText92")],
          page: (page, pages) =>
            t("assetDashboardText103").replace("%1", page).replace("%2", pages),
        },
      });
      setPicking(false);
    } finally {
      setBusy(false);
    }
  };

  /*
    One renderer for both ends of the range: they differ only in their label,
    which preset is the open-ended one, and where the value goes.
  */
  const renderRange = (
    labelKey,
    openKey,
    openLabelKey,
    preset,
    setPreset,
    custom,
    setCustom,
    bounds = {},
  ) => {
    /*
      A preset that would land outside the other end is offered greyed.

      The date field is bounded, but these are the other way into the same
      value: every preset here except the open-ended one resolves to a date
      in the PAST, so "last 6 months" as the end of a range starting later
      than that is exactly the backwards window the bounds exist to stop.

      "Custom" is never disabled -- it is not a date, it opens the field
      that is already bounded. Nor is the open-ended one, which resolves to
      "" at this end and means "no limit".
    */
    const outOfRange = (key) => {
      if (key === RANGE_CUSTOM) return false;
      const date = resolveRangeDate(key, "");
      if (!date) return false;
      if (bounds.min && date < bounds.min) return true;
      if (bounds.max && date > bounds.max) return true;
      return false;
    };
    return (
    <div className={styles.field}>
      <div className={styles.field_label}>{t(labelKey)}</div>
      {/*
        The same field as a line of type rather than a control. A document has
        no dropdowns to open, and the one it would be showing is a preset whose
        meaning expires; see `rangeText`. Drawn only while capturing, which is
        also when the control beside it is hidden.
      */}
      <div className={styles.field_static}>
        {rangeText(preset, custom, openLabelKey)}
      </div>
      {preset === RANGE_CUSTOM ? (
        <div className={styles.custom_wrap}>
          {/*
            Ours, not the browser's. A native date input is drawn in the
            BROWSER's locale and shows DD/MM even with the site in English --
            `lang` on the input and on an ancestor were both tried and neither
            moves it. Same box, same class; only the wording is ours now.
          */}
          <ReportDateField
            autoFocus
            className={styles.control}
            value={custom}
            onChange={setCustom}
            french={websiteCurrentLang?.label === "fr"}
            /*
              Bounded by the OTHER end of the range, so a window that runs
              backwards cannot be asked for in the first place. The days
              outside it are greyed rather than merely refused on submit.
            */
            minValue={bounds.min}
            maxValue={bounds.max}
            ariaLabel={t(labelKey)}
          />
          {/* Back to the presets, discarding the date. */}
          <button
            type="button"
            className={styles.custom_clear}
            aria-label={t(openLabelKey)}
            onClick={() => {
              setCustom("");
              setPreset(openKey);
            }}
          />
        </div>
      ) : (
        <select
          className={styles.control}
          value={preset}
          onChange={(event) => setPreset(event.target.value)}
        >
          <option value={openKey}>{t(openLabelKey)}</option>
          <option value="last12" disabled={outOfRange("last12")}>
            {t("assetDashboardText9")}
          </option>
          <option value="last6" disabled={outOfRange("last6")}>
            {t("assetDashboardText10")}
          </option>
          <option value="ytd" disabled={outOfRange("ytd")}>
            {t("assetDashboardText11")}
          </option>
          <option value={RANGE_CUSTOM}>{t("assetDashboardText12")}</option>
        </select>
      )}
    </div>
    );
  };

  /*
    Docked into the profile's header when there is one to dock into, and drawn
    in place otherwise. The fallback matters: if the slot were ever missing the
    button would simply vanish, and losing the only way to produce the PDF is a
    worse failure than it being in the wrong place.
  */
  const reportButton = (
    <button
      type="button"
      className={`${amStyles.report_button} ${
        headerSlot ? amStyles.report_button_docked : ""
      }`}
      disabled={busy}
      onClick={() => setPicking(true)}
    >
      {tAm("assetManagementText2", "Download Financial Report")}
    </button>
  );

  return (
    <div className={amStyles.page}>
      {headerSlot && createPortal(reportButton, headerSlot)}
      <div
          className={`${amStyles.title_row} ${
            /* same 60/60 the other partner page titles get */
            partnerView && !inCustomerProfile
              ? partnerNavStyles.partner_title_row
              : ""
          }`}
        >
        {/* The profile supplies the heading when this page is shown inside one. */}
        {!inCustomerProfile && (
          <h1 className={amStyles.page_title}>
            {tAm("assetManagementText1", "Asset Management")}
          </h1>
        )}
        {/*
          The report as a PDF. Everything on this page is decrypted in the
          browser and nowhere else, so the document has to be produced here —
          there is no server-side render of it to ask for.

          The button no longer produces the file: it opens the chapter picker,
          and `generate` below is what the picker calls.
        */}
        {!headerSlot && reportButton}
      </div>

      {/* Top-level navigation sits above the page's own crumbs. */}
      <PageTabs />

      {/* Asset Management's own crumbs, with this page as the active one. */}
      <div className={amStyles.breadcrumb}>
        {/* Same order as the Asset Management page; only the active one differs. */}
        <button
          type="button"
          className={amStyles.crumb}
          /*
            The way back, and it has to stay inside a customer's profile. The
            absolute path left it and landed on the PARTNER's own asset
            management -- the same fault the other breadcrumb had, one screen
            further along.
          */
          onClick={() =>
            navigate(
              customerId
                ? customerPath(customerId, "assetmanagement")
                : "/user/assetmanagement"
            )
          }
        >
          {tAm("assetManagementText3", "Asset Management Page")}
        </button>
        <span className={amStyles.crumb_separator}>|</span>
        <span className={`${amStyles.crumb} ${amStyles.crumb_active}`}>
          {tAm("assetManagementText12", "Wealth Report")}
        </span>
      </div>

      {/*
        What goes into the PDF: this card, with the report's title and the range
        it was run for, down to the last section. The page title and crumbs
        above are the app's furniture, not part of the document.
      */}
      <div ref={reportRef}>
        <div className={styles.card}>
          <div className={styles.card_left}>
            <div className={styles.brand}>
              <span className={styles.brand_dark}>OSTRE</span>
              <span className={styles.brand_blue}>ON</span>
            </div>
            <div className={styles.brand_sub}>{t("assetDashboardText3")}</div>

            <h2 className={styles.report_title}>{t("assetDashboardText1")}</h2>
            <p className={styles.report_subtitle}>{t("assetDashboardText2")}</p>

            <div className={styles.fields}>
              {/*
                Each end is capped by the other.

                `resolveRangeDate` is what the report itself reads, so a
                preset bounds the opposite field exactly as a typed date
                does -- picking "last 6 months" as the start greys out
                everything before it on the end. An open-ended end resolves
                to "" and bounds nothing.
              */}
              {renderRange(
                "assetDashboardText4",
                RANGE_START,
                "assetDashboardText7",
                fromPreset,
                setFromPreset,
                fromCustom,
                setFromCustom,
                { max: resolveRangeDate(tillPreset, tillCustom) },
              )}
              {renderRange(
                "assetDashboardText5",
                RANGE_NOW,
                "assetDashboardText8",
                tillPreset,
                setTillPreset,
                tillCustom,
                setTillCustom,
                { min: resolveRangeDate(fromPreset, fromCustom) },
              )}

              <div className={`${styles.field} ${styles.field_currency}`}>
                <div className={styles.field_label}>
                  {t("assetDashboardText6")}
                </div>
                <div className={styles.field_static}>
                  {(
                    CURRENCY_OPTIONS.find((o) => o.value === currency) || {}
                  ).label || String(currency).toUpperCase()}
                </div>
                <select
                  className={styles.control}
                  value={currency}
                  onChange={(event) => setCurrency(event.target.value)}
                >
                  {CURRENCY_OPTIONS.map((option) => (
                    <option key={option.value} value={option.value}>
                      {option.label}
                    </option>
                  ))}
                </select>
              </div>
            </div>
          </div>

          <div className={styles.card_right}>
            <div className={styles.client_name}>{fullName}</div>
            <div className={styles.client_row}>
              <div className={styles.avatar}>
                {avatar ? (
                  <img
                    className={styles.avatar_img}
                    src={`data:image/${extension};base64,${avatar}`}
                    alt=""
                  />
                ) : (
                  <span className={styles.avatar_initials}>{initials}</span>
                )}
              </div>

              <div className={styles.counts}>
                {counts.map((item) => (
                  <button
                    key={item.key}
                    type="button"
                    className={styles.count}
                    onClick={() => navigate(item.to)}
                  >
                    <span className={`${styles.count_icon} ${item.icon}`} />
                    <span className={styles.count_text}>
                      {item.value} {item.label}
                    </span>
                  </button>
                ))}
              </div>
            </div>
          </div>

          {/*
            The design's third column: the date the document was issued, behind
            a hairline rule.

            Drawn only into the PDF. On screen the report is not a document —
            it is whatever the filters currently say, and it changes as they are
            moved — so stamping it with an issue date there would be a claim the
            page cannot keep. It is revealed on the copy that is photographed,
            never on this one, so the reader never sees the card reflow.
          */}
          <div className={styles.card_meta}>
            <div className={styles.meta_line}>
              {t("assetDashboardText102")} {reportDate}
            </div>
          </div>
        </div>

        {/*
        The report's body. Numbered here rather than inside each section, so the
        running order is one list and stays in step if a section moves.

        The four category sections are the same component: the design draws Real
        estate, Financial, Private equity and Crypto identically, differing only
        in their title and whether each row carries a change.
      */}
        <ExecutiveSummary number={1} range={range} rates={rates} />
        <WealthSummary number={2} range={range} rates={rates} />
        {/*
        One section per asset type the account actually holds, so how many there
        are is not known until the assets are read. The sections after them are
        numbered from however many appeared, which is why the count comes back
        out rather than being assumed.
      */}
        <ExposureSections
          startNumber={3}
          range={range}
          rates={rates}
          onSections={setExposureSections}
        />
        <IncomeSection number={3 + exposureCount} range={range} rates={rates} />
        <EstateSection number={4 + exposureCount} range={range} rates={rates} />
        <RecommendationsSection number={5 + exposureCount} />
      </div>

      {/*
        Outside the captured element, so it can never end up photographed into
        the document it is choosing the contents of.
      */}
      {picking && (
        <SectionSelector
          sections={reportChapters}
          title={t("assetDashboardText104")}
          intro={t("assetDashboardText105")}
          noLabel={t("assetDashboardText106")}
          yesLabel={t("assetDashboardText107")}
          closeLabel={t("assetDashboardText108")}
          generateLabel={tAm(
            "assetManagementText2",
            "Download Financial Report"
          )}
          busyLabel={tAm("assetManagementText41", "Preparing report...")}
          busy={busy}
          onClose={() => setPicking(false)}
          onGenerate={generate}
        />
      )}
    </div>
  );
}

export default AssetDashboard;
